"use client";
import Head from "next/head";
import Script from "next/script";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Counter from "@/components/counter";
import { ServiceCard } from "@/components/serviceCard";
import { useLocale } from "next-intl";
import { FloatingNav } from "@/components/ui/FloatingNavbar";
export const runtime = "edge";

export default function ServicePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const locale = useLocale();
  // Animation Variants
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const containerStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2, delayChildren: 0.1 } },
  };

  // Hook for scroll trigger
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <Head>
        <title>Service - TRQ Agency</title>
        <meta
          name="description"
          content="TRQ Agency is a creative studio specializing in branding, web design, development, and digital transformation."
        />
      </Head>
      <FloatingNav />
      {/* === Hero Section === */}
      <section className="black-hero-02">
        <motion.div
          className="black-hero-container-02"
          variants={containerStagger}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="hero-text-wrap-02" variants={containerStagger}>
            <div className="hero-title-02">
              <motion.div className="overflow-hidden" variants={fadeUp}>
                <h1 className="heading-01">Expert</h1>
              </motion.div>
              <motion.div className="overflow-hidden" variants={fadeUp}>
                <h1 className="heading-01">Solutions</h1>
              </motion.div>
              <motion.div className="overflow-hidden" variants={fadeUp}>
                <h1 className="heading-01">Designed</h1>
              </motion.div>
            </div>

            <motion.div className="hero-short-text-02" variants={fadeUp}>
              <div className="body-01">
                Crafting brands building experiences for driving results.
              </div>
            </motion.div>

            <motion.div className="mobile-btn" variants={fadeUp}>
              <div className="secondary-btn-wrap">
                <Link href="/contract-us" className="secondary-btn">
                  <div className="primary-btn-inner">
                    <div className="btn-arrow-wrap _01">
                      <Image
                        src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/67d7308cf20915107b6fdfed_arrow-down-01-1.svg"
                        alt=""
                        className="btn-arrow-img hover"
                        width={20}
                        height={20}
                      />
                      <Image
                        src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/67d7308cf20915107b6fdfed_arrow-down-01-1.svg"
                        alt=""
                        className="btn-arrow-img"
                        width={20}
                        height={20}
                      />
                    </div>
                    <div className="btn-text-wrap">
                      <p className="sub-title-semibold">Contact Us</p>
                      <p className="sub-title-semibold hover">Contact Us</p>
                    </div>
                  </div>
                </Link>
              </div>
            </motion.div>
          </motion.div>

          <div className="hero-bottom-02">
            <div className="item-box">
              <div className="circleX green"></div>
              <div className="sub-title">Riyadh, {currentTime}</div>
            </div>
            <div className="scroll-text-wrap">
              <div className="scroll-icon-wrap">
                <img
                  src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/6804b69a16f35964874c0550_Group%202147225222.svg"
                  loading="lazy"
                  alt=""
                  className="scroll-icon _01"
                />
                <img
                  src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/6804b69a16f35964874c0550_Group%202147225222.svg"
                  loading="lazy"
                  alt=""
                  className="scroll-icon _02"
                />
              </div>
              <div className="sub-title">Scroll Down</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* === Services Section === */}
      <section className="service-01" ref={sectionRef}>
        <motion.div
          className="service-01-container"
          variants={containerStagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="section-header" variants={fadeUp}>
            <div className="section-heade-titile">
              <div className="heading-04">Services We Provide -</div>
            </div>
            <div className="section-header-text max-767">
              <motion.div className="overflow-hidden" variants={fadeUp}>
                <h2 className="heading-02 uper-case">Expert Services</h2>
              </motion.div>
              <motion.div className="overflow-hidden" variants={fadeUp}>
                <h2 className="heading-02 uper-case">to Drive Success</h2>
              </motion.div>
            </div>
          </motion.div>

          <motion.div className="service-grid" variants={containerStagger}>
            {/* Satisfaction Card */}
            <motion.div className="state-card-02 _02" variants={fadeUp}>
              <div>
                <div className="state-card-03">
                  <div className="statistics-number-wrap _03">
                    <Counter
                      to={100}
                      duration={1.4}
                      suffix="%"
                      className="heading-01"
                    />
                  </div>
                </div>
                <div className="body-02 mt-10">Client Satisfaction Rate</div>
              </div>
            </motion.div>
            {/* Service Cards */}
            <motion.div variants={fadeUp}>
              <ServiceCard
                icon="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65836/67fc9fb5b2ae65e17cf9dae5_Style%3DStroke%2C%20Type%3DRounded.svg"
                title="Branding"
                description="We master the art of shaping a unique identity..."
                link={`/${locale}/services/branding`}
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <ServiceCard
                icon="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65836/67fc9fd52ea14342e38c6545_ai-brain-04.svg"
                title="App Design"
                description="We focus on creating user-friendly interfaces..."
                link={`/${locale}/services/app-design`}
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <ServiceCard
                icon="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65836/67fca0a883eecfa712fc3095_ai-laptop.svg"
                title="Web Design"
                description="Streamline processes with AI-powered HR solutions..."
                link={`/${locale}/services/web-development`}
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <ServiceCard
                icon="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65836/680c92c24508a8078761ff49_command-line.svg"
                title="Development"
                description="Turning design concepts into functional software."
                link={`/${locale}/services/development`}
              />
            </motion.div>

            {/* Contact Card */}
            <motion.div className="contact-card" variants={fadeUp}>
              <div className="body-03 gray-color">
                Enable businesses to thrive online by delivering tailored web
                development
              </div>
              <div className="service-btn-wrap">
                <div className="secondary-btn-wrap">
                  <Link href={`/${locale}/services`} className="secondary-btn">
                    <div className="primary-btn-inner">
                      <div className="btn-text-wrap">
                        View All Services
                        <p className="sub-title-semibold hover">
                          View All Services
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ProcessSection + SolvingSection → wrap headings, cards, and boxes in motion.div with fadeUp */}
      <ProcessSection />
      <SolvingSection />
      {/* Footer Section */}
      <section className="footer-cta">
        <motion.div
          className="footer-cta-container"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <div className="cta">
            <h2 className="heading-01">Let&apos;s work Together</h2>
            <Link
              href="/contract-us"
              className="cta-float-wrap w-inline-block w--current"
            >
              <div className="cta-float">
                <Image
                  src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/67d7308c4eb472a0833d503b_arrow-down-01.svg"
                  alt=""
                  className="cta-float-icon"
                  width={20}
                  height={20}
                />
              </div>
            </Link>
          </div>

          {/* Dark footer content */}
          <motion.div
            className="footer-dark"
            variants={containerStagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <div className="footer-container">
              <div className="footer-wrapper">
                <motion.div className="footer-left" variants={fadeUp}>
                  <Link href="/" className="footer-brand w-inline-block">
                    <Image
                      src="/mainLogo.png"
                      alt="logo"
                      height={70}
                      width={70}
                    />
                  </Link>
                  <div className="body-03 mt-20 gray-color">
                    Connect with us:
                  </div>
                  <div className="footer-social-block">
                    <a
                      href="https://dribbble.com/"
                      target="_blank"
                      className="footer-social-link w-inline-block"
                    >
                      <img
                        src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/67d730aa155b1f0c122f2b39_dribble%201.svg"
                        loading="lazy"
                        alt=""
                        className="social-icon"
                      />
                    </a>
                    <a
                      href="https://www.facebook.com/"
                      target="_blank"
                      className="footer-social-link w-inline-block"
                    >
                      <img
                        src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/67d73095c435083e21c1093d_dribble%201.svg"
                        loading="lazy"
                        alt=""
                        className="social-icon"
                      />
                    </a>
                    <a
                      href="https://x.com/"
                      target="_blank"
                      className="footer-social-link w-inline-block"
                    >
                      <img
                        src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/67d730aa51a3e92332577377_twitter.svg"
                        loading="lazy"
                        alt=""
                        className="social-icon"
                      />
                    </a>
                    <a
                      href="https://www.instagram.com/"
                      target="_blank"
                      className="footer-social-link w-inline-block"
                    >
                      <img
                        src="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/67d738d1155b1f0c1234fc90_Group%202147225221.svg"
                        loading="lazy"
                        alt=""
                        className="social-icon"
                      />
                    </a>
                  </div>
                </motion.div>

                <div className="footer-content">
                  <motion.div className="footer-block" variants={fadeUp}>
                    <div className="heading-04">Pages</div>
                    <Link href="/" className="footer-link">
                      Home
                    </Link>
                    <Link href="/about-us" className="footer-link">
                      About Us
                    </Link>
                    <Link href="/service" className="footer-link">
                      Our Service
                    </Link>
                    <Link href="/work" className="footer-link">
                      Our Works
                    </Link>
                    <Link href="/blog" className="footer-link">
                      Blog
                    </Link>
                    <Link
                      href="/contract-us"
                      className="footer-link w--current"
                    >
                      Contact us
                    </Link>
                  </motion.div>

                  <motion.div className="footer-block" variants={fadeUp}>
                    <div className="heading-04">Utility Pages</div>
                    <a href="/404" className="footer-link">
                      404
                    </a>
                    <a href="/reference/style-guide" className="footer-link">
                      Styleguide
                    </a>
                    <a href="/reference/license" className="footer-link">
                      License
                    </a>
                    <a href="/reference/changelog" className="footer-link">
                      Changelog
                    </a>
                    <a href="/401" className="footer-link">
                      Password
                    </a>
                  </motion.div>

                  <motion.div className="footer-block _03" variants={fadeUp}>
                    <div className="contact-block gray-color"></div>
                    <div className="contact-block gray-color">
                      <div className="body-02">Email:</div>
                      <a
                        href="mailto:info@trq-agency.com"
                        className="contact-link hover-effect"
                      >
                        info@trq-agency.com
                      </a>
                    </div>
                  </motion.div>
                </div>
              </div>

              <motion.div className="footer-divider" variants={fadeUp} />

              <motion.div className="footer-bottom" variants={fadeUp}>
                <div className="footer-copyright-center">
                  Copyright 2025. All rights reserved. Powered by{" "}
                  <a
                    href="https://trq-agency.com/"
                    target="_blank"
                    className="privacy-terms-link"
                  >
                    TRQ-Agency LTD
                  </a>
                  .
                </div>
                <div className="privacy-terms-wrap">
                  <a href="/privacy-policy" className="privacy-terms-link">
                    Privacy Policy
                  </a>
                  <div>|</div>
                  <a href="/terms" className="privacy-terms-link">
                    Terms of Conditions
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </section>
    </>
  );
}

// ServiceCard Component (unchanged design, just wrapped by motion in parent)

// ProcessSection component
function ProcessSection() {
  return (
    <section className="process">
      <div className="process-container">
        <div className="section-header">
          <div className="section-heade-titile">
            <div className="heading-04">Simple process -</div>
          </div>
          <div className="section-header-text max-778">
            <div className="heading-02 uper-case">
              Clear, &amp;Effective Workflow
            </div>
          </div>
        </div>
        <div className="process-card-wrapper">
          <ProcessCard
            icon="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/67d7de217811444080ae113d_Style%3DStroke%2C%20Type%3DRounded%20(1).svg"
            step="01"
            title="Analysis to drive insights"
            description="We dive deep into data to uncover patterns, trends, opportunities, every decision is backed by strategic insights."
          />
          <ProcessCard
            icon="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/6804d882e59f5474075ae942_ai-innovation-03.svg"
            step="02"
            title="Validation for reliable results"
            description="Thorough testing ensures every solution performs with accuracy and consistency. We don't guess — we prove, refine."
          />
          <ProcessCard
            icon="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/6804d88258514b6a49fbfb5d_keyframes-double.svg"
            step="03"
            title="Engaging User Interface Designs"
            description="Our interfaces are built to delight, guide, and convert effortlessly, visually appealing and intuitively crafted for every user."
          />
          <ProcessCard
            icon="https://cdn.prod.website-files.com/67c3581ddb64922e2ab65814/6804d88283c4661ee0218232_floppy-disk.svg"
            step="04"
            title="Reality with Expert Implementation"
            description="From concept to execution, we bring ideas to life with precision, hands-on expertise ensures seamless, real-world."
          />
        </div>
      </div>
    </section>
  );
}

// ProcessCard component
function ProcessCard({ icon, step, title, description }: any) {
  return (
    <div className="process-card">
      <Image
        src={icon}
        alt=""
        className="process-icon"
        width={50}
        height={50}
      />
      <div className="process-text-wrap">
        <div className="step-tag">
          <div className="sub-title">Step -{step}</div>
        </div>
        <div className="heading-04">{title}</div>
        <div className="process-body">
          <div className="body-03 mt-20 gray-color">{description}</div>
        </div>
      </div>
    </div>
  );
}
// SolvingSection component
function SolvingSection() {
  return (
    <section className="solving">
      <div className="solving-container">
        <div className="section-header">
          <div className="section-heade-titile">
            <div className="heading-04">Problem-Solving -</div>
          </div>
          <div className="section-header-text">
            <div className="heading-03 cap">
              Unlocking Solutions with our Ingenious Thinking
            </div>
            <div className="body-02 gray-color mt-20">
              We approach every challenge with curiosity, creativity, and
              strategic insight. By blending innovation with functionality, we
              develop solutions that truly make an impact.
            </div>
          </div>
        </div>
        <div className="solving-wrapper">
          <div className="solving-card">
            <div className="heading-04">
              Track, Analyze, and Enhance Performance Effectively
            </div>
            <div className="body-03 gray-color">
              We transform data into action by monitoring key metrics that drive
              growth. From engagement convert, every insight helps refine.
            </div>
          </div>
          <div className="solving-img">
            <Image
              src="/mainLogo.png"
              alt=""
              className="full-img"
              width={1160}
              height={600}
            />
            <div className="image-overlay"></div>
          </div>
          <div className="solving-card">
            <div className="heading-04">
              Dedicated to Providing Superior Customer Assistance
            </div>
            <div className="body-03 gray-color">
              Our support goes beyond solving issues — it's about building
              relationships. We're here with timely responses, expert guidance.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
